import { ethers } from "ethers";
import type { TokenInfo } from "../../config";
import type { FeeAction, PrivateFeePolicy } from "../hooks/usePrivateFeePolicy";
import { privateFeeAmounts } from "../components/PrivateFeeSummary";

export function isPaymentToken(token: TokenInfo): boolean {
  if (
    token.tokenType === "erc721" ||
    token.tokenType === "erc1155" ||
    token.tokenType === "vnft"
  )
    return false;
  return !!token.isNative || !token.tokenType || token.tokenType === "erc20";
}

/** Keep comparisons in base units: floating point rounds small balances and MAX. */
export function amountValidation(params: {
  amount: string;
  decimals: number;
  symbol: string;
  balance: string | bigint | null;
  privateBalance?: boolean;
  native?: boolean;
  maxDeposit?: bigint;
  policy?: PrivateFeePolicy | null;
  action?: FeeAction;
  deductedFee?: (amount: bigint) => bigint;
  /** Set while a live relayer fee quote is loading or unavailable. */
  feePending?: string | null;
}): { value: bigint | null; error: string | null } {
  const fail = (error: string) => ({ value: null, error });
  const input = params.amount.trim();
  if (!input || !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(input))
    return fail("Enter a valid amount greater than zero.");
  let value: bigint;
  try {
    value = ethers.parseUnits(input, params.decimals);
  } catch {
    return fail(
      `Enter an amount with no more than ${params.decimals} decimal places.`,
    );
  }
  if (value <= 0n) return fail("Enter an amount greater than zero.");
  if (params.balance === null) return fail("Checking available balance…");
  let balance: bigint;
  try {
    balance =
      typeof params.balance === "bigint"
        ? params.balance
        : ethers.parseUnits(params.balance, params.decimals);
  } catch {
    return fail("Unable to check your balance. Refresh and try again.");
  }
  if (value > balance)
    return fail(
      `Insufficient ${params.privateBalance ? "private " : ""}${params.symbol} balance.`,
    );
  if (
    params.native &&
    (value === balance ||
      (params.maxDeposit !== undefined && value > params.maxDeposit))
  )
    return fail(
      `Leave enough ${params.symbol} for the network fee. Use MAX to calculate the available amount.`,
    );
  if (params.policy && params.action) {
    try {
      if (privateFeeAmounts(params.policy, params.action, input).net <= 0n)
        return fail("This amount does not cover the transaction fees.");
    } catch {
      return fail("Unable to check transaction fees. Refresh and try again.");
    }
  }
  if (params.feePending) return fail(params.feePending);
  if (params.deductedFee && value <= params.deductedFee(value))
    return fail("This amount must exceed the relayer fee.");
  return { value, error: null };
}
